import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Radar, Users, Mail, Phone, MapPin, FileText, Star, X, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import TrialBanner from "@/components/TrialBanner";
import CandidateRadar from "@/components/CandidateRadar";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["submitted", "reviewing", "accepted", "rejected"];
const STATUS_COLOR = {
  submitted: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30",
  reviewing: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  accepted: "text-teal-300 bg-teal-500/10 border-teal-500/30",
  rejected: "text-red-300 bg-red-500/10 border-red-500/30",
};

export default function Applicants() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [jobFilter, setJobFilter] = useState("all");
  const [onlyShort, setOnlyShort] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/employer/jobs").then((r) => setJobs(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = jobFilter !== "all" ? { job_id: jobFilter } : {};
    api.get("/employer/applicants", { params }).then((r) => setApplicants(r.data)).finally(() => setLoading(false));
  }, [jobFilter]);

  const patch = (id, changes) => {
    setApplicants((prev) => prev.map((a) => (a.id === id ? { ...a, ...changes } : a)));
    setSelected((s) => (s && s.id === id ? { ...s, ...changes } : s));
  };

  const toggleShortlist = async (a, e) => {
    e?.stopPropagation();
    try {
      const { data } = await api.post(`/applications/${a.id}/shortlist`);
      patch(a.id, { shortlisted: data.shortlisted });
      toast.success(data.shortlisted ? t("status.shortlisted") : t("status.allCandidates"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (a, status) => {
    try {
      await api.put(`/applications/${a.id}/status`, { status });
      patch(a.id, { status });
      toast.success(t(`status.${status}`));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const viewResume = (a) => {
    if (!a.applicant?.resume_url) return;
    const token = localStorage.getItem("wo_token");
    const path = a.applicant.resume_url.replace(/^\/api\/files\//, "");
    window.open(`${BACKEND_URL}/api/files/${path}?auth=${token}`, "_blank");
  };

  const shown = onlyShort ? applicants.filter((a) => a.shortlisted) : applicants;

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400"><Radar size={20} /></div>
            <div>
              <h1 className="font-display font-bold text-3xl text-slate-100">{t("page.applicantRadar")}</h1>
              <p className="text-slate-400 text-sm"><span className="font-mono text-teal-400" data-testid="applicant-count">{shown.length}</span> {t("page.plotted")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setOnlyShort(!onlyShort)} data-testid="filter-shortlisted"
              className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors ${onlyShort ? "text-amber-300 border-amber-500/40 bg-amber-500/10" : "text-slate-300 border-white/10 hover:bg-white/5"}`}>
              <Star size={15} fill={onlyShort ? "currentColor" : "none"} /> {t("status.onlyShortlisted")}
            </button>
            <Select value={jobFilter} onValueChange={setJobFilter}>
              <SelectTrigger data-testid="applicants-job-filter" className="w-56 bg-slate-950/40 border-white/10 text-slate-200"><SelectValue /></SelectTrigger>
              <SelectContent className="text-slate-200">
                <SelectItem value="all">{t("page.allPostings")}</SelectItem>
                {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.title} ({j.applicant_count})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <TrialBanner />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
          <div className="liquid-glass rounded-2xl p-6 sm:p-10">
            {loading ? (
              <p className="text-slate-500 text-center py-20">{t("radar.scanning")}</p>
            ) : shown.length === 0 ? (
              <div className="text-center py-24 text-slate-500" data-testid="applicants-empty">
                <Users size={40} className="mx-auto text-slate-600 mb-4" />
                {t("page.noApplicants")}
              </div>
            ) : (
              <CandidateRadar applicants={shown} onSelect={setSelected} selectedId={selected?.id} />
            )}
            <div className="flex flex-wrap gap-4 mt-6 justify-center">
              {[["#2DD4BF", "90%+"], ["#06B6D4", "80–89%"], ["#3B82F6", "70–79%"], ["#F59E0B", "<70%"]].map(([c, l]) => (
                <span key={l} className="flex items-center gap-1.5 text-xs text-slate-400"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c, boxShadow: `0 0 6px ${c}` }} /> {l}</span>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-display font-semibold text-slate-200 flex items-center gap-2"><Users size={16} /> {t("page.candidates")}</h3>
            <div className="space-y-3 max-h-[720px] overflow-y-auto pr-1">
              {shown.map((a) => (
                <div key={a.id} role="button" tabIndex={0} onClick={() => setSelected(a)} data-testid="applicant-list-item"
                  className={`w-full text-left cursor-pointer rounded-xl border p-4 transition-colors ${selected?.id === a.id ? "border-teal-500/50 bg-teal-500/5" : "border-white/10 bg-slate-900/50 hover:border-teal-500/30"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-blue-600 flex items-center justify-center text-sm font-bold text-slate-900">
                        {a.applicant?.name?.[0]?.toUpperCase() || "A"}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-100 truncate">{a.applicant?.name}</div>
                        <div className="text-xs text-slate-400 truncate">{a.applicant?.headline || a.job_title}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={(e) => toggleShortlist(a, e)} data-testid="shortlist-toggle"
                        className={`p-1 rounded transition-colors ${a.shortlisted ? "text-amber-400" : "text-slate-500 hover:text-amber-400"}`}>
                        <Star size={16} fill={a.shortlisted ? "currentColor" : "none"} />
                      </button>
                      <span className="font-mono text-sm text-teal-300">{a.match_score || 0}%</span>
                    </div>
                  </div>
                  <span className={`inline-block mt-2 text-[11px] px-2 py-0.5 rounded-full border ${STATUS_COLOR[a.status || "submitted"]}`}>{t(`status.${a.status || "submitted"}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="absolute right-0 top-0 h-full w-full max-w-md liquid-glass border-l border-white/10 overflow-y-auto p-6 sm:p-8" data-testid="applicant-detail-drawer">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-400 to-blue-600 flex items-center justify-center text-xl font-bold text-slate-900">
                  {selected.applicant?.name?.[0]?.toUpperCase() || "A"}
                </div>
                <div>
                  <h2 className="font-display font-bold text-lg text-slate-100">{selected.applicant?.name}</h2>
                  <p className="text-slate-400 text-sm">{selected.applicant?.headline}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={(e) => toggleShortlist(selected, e)} data-testid="drawer-shortlist"
                  className={`p-2 rounded-lg ${selected.shortlisted ? "text-amber-400" : "text-slate-400 hover:text-amber-400"}`}>
                  <Star size={18} fill={selected.shortlisted ? "currentColor" : "none"} />
                </button>
                <button onClick={() => setSelected(null)} className="p-2 text-slate-400 hover:text-white"><X size={20} /></button>
              </div>
            </div>

            <div className="mt-5 rounded-xl bg-slate-950/40 border border-teal-500/15 p-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">{t("status.matchScore")}</div>
                <div className="text-teal-300 font-mono text-2xl font-bold">{selected.match_score}%</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">{t("status.appliedFor")}</div>
                <div className="text-slate-200 text-sm">{selected.job_title}</div>
              </div>
            </div>

            {/* status control */}
            <div className="mt-5">
              <div className="text-xs text-slate-500 font-mono uppercase tracking-wider mb-2">{t("status.setStatus")}</div>
              <div className="grid grid-cols-2 gap-2" data-testid="status-controls">
                {STATUSES.map((st) => (
                  <button key={st} onClick={() => setStatus(selected, st)} data-testid={`set-status-${st}`}
                    className={`flex items-center justify-center gap-1.5 text-sm py-2 rounded-lg border transition-colors ${selected.status === st ? STATUS_COLOR[st] : "text-slate-300 border-white/10 hover:bg-white/5"}`}>
                    {selected.status === st && <Check size={13} />} {t(`status.${st}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 space-y-2.5 text-sm">
              {selected.applicant?.email && <Contact icon={Mail} value={selected.applicant.email} />}
              {selected.applicant?.phone && <Contact icon={Phone} value={selected.applicant.phone} />}
              {selected.applicant?.location && <Contact icon={MapPin} value={selected.applicant.location} />}
            </div>

            {selected.applicant?.skills?.length > 0 && (
              <div className="mt-5">
                <h4 className="font-display font-semibold text-slate-200 mb-2 text-sm">{t("status.skills")}</h4>
                <div className="flex flex-wrap gap-2">
                  {selected.applicant.skills.map((s) => <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-slate-800/80 text-cyan-300 border border-cyan-500/20">{s}</span>)}
                </div>
              </div>
            )}

            {selected.cover_note && (
              <div className="mt-5">
                <h4 className="font-display font-semibold text-slate-200 mb-2 text-sm">{t("status.coverNote")}</h4>
                <p className="text-slate-400 text-sm">{selected.cover_note}</p>
              </div>
            )}

            <div className="mt-6">
              {selected.applicant?.resume_url ? (
                <Button onClick={() => viewResume(selected)} data-testid="view-resume-button"
                  className="w-full bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold hover:opacity-90 py-6">
                  <FileText size={16} className="mr-2" /> {t("status.viewResume")} ({selected.applicant.resume_name})
                </Button>
              ) : (
                <div className="text-center text-sm text-slate-500 py-3 border border-white/10 rounded-lg">{t("status.noResume")}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Contact({ icon: Icon, value }) {
  return (
    <div className="flex items-center gap-2 text-slate-300">
      <Icon size={14} className="text-teal-400 shrink-0" /> <span className="truncate">{value}</span>
    </div>
  );
}
